import {
  Directive,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Output,
} from '@angular/core';

@Directive({
  selector: '[smoothSize]',
  standalone: true,
})
export class SmoothSizeDirective implements OnDestroy {
  public readonly smoothHeight: InputSignal<boolean> = input<boolean>(false);
  public readonly smoothWidth: InputSignal<boolean> = input<boolean>(false);
  public readonly duration: InputSignal<number> = input<number>(150);
  public readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  @Output() public readonly finishAnimation = new EventEmitter<'height' | 'width'>();

  private observerHeight: ResizeObserver;
  private observerWidth: ResizeObserver;

  private readonly observeSmoothHeight = effect(() => {
    const element = this.elementRef.nativeElement;
    if (!element) return;

    if (!this.observerHeight) this.observerHeight = this.getObserverSize('height');

    const smoothHeight = this.smoothHeight();
    if (smoothHeight) {
      this.observerHeight.observe(element);
      return;
    }
    this.observerHeight.unobserve(element);
  })

  private readonly observeSmoothWidth = effect(() => {
    const element = this.elementRef.nativeElement;
    if (!element) return;

    if (!this.observerWidth) this.observerWidth = this.getObserverSize('width');

    const smoothWidth = this.smoothWidth();
    if (smoothWidth) {
      this.observerWidth.observe(element);
      return;
    }
    this.observerWidth.unobserve(element);
  })

  public ngOnDestroy(): void {
    // Remover o host do DOM zera as suas dimensões, o que é uma mudança de
    // tamanho como qualquer outra: sem `disconnect`, os observers sobrevivem à
    // diretiva e ainda entregam essa última notificação (segurando `this` e o
    // elemento junto).
    this.observerHeight?.disconnect();
    this.observerWidth?.disconnect();
    this.elementRef?.nativeElement?.getAnimations?.()?.forEach(animation => animation?.cancel());
  }

  private getObserverSize(size: 'height' | 'width'): ResizeObserver {
    const element = this.elementRef.nativeElement;
    let isAnimating = false;
    let lastSize: number;
    return new ResizeObserver(() => {

      const contentSize = element.getBoundingClientRect()[size];
      // `document.body` é null enquanto o documento é descartado (navegação /
      // unload). O `ngOnDestroy` não cobre esse caso — numa saída de página ele
      // não chega a correr, mas o observer ainda dispara com o body já removido.
      const elementInDOM = !!document.body?.contains(element);
      const isVisible = element.checkVisibility?.() ?? true;
      if (!elementInDOM || !isVisible || isAnimating || lastSize === contentSize || typeof contentSize !== 'number' || (typeof lastSize !== 'number' && !contentSize)) return;

      if (typeof lastSize === 'number') {
        isAnimating = true;

        element.animate([{ [size]: `${lastSize}px` }, { [size]: `${contentSize}px` }], {
          duration: this.duration(),
          easing: 'ease-in-out'
        }).onfinish = () => {
          isAnimating = false;
          lastSize = contentSize;
          this.finishAnimation.emit(size);
        };

        return;
      }

      lastSize = contentSize;
    });
  }
}
